import React from "react";
import { Typography, Box, IconButton } from "@mui/material";
import {
  LockOutlined,
  PlayArrowRounded,
  Favorite,
  FavoriteBorder,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { usePricingNav } from "../../hooks/usePricingNav";
import {
  VideoCardWrapper,
  Thumbnail,
  DurationTag,
  MEMBERSHIP_COLORS,
} from "./MembershipStyles";
import { useFavorites } from "../../contexts/FavoritesContext";

interface VideoCardProps {
  id: string;
  title: string;
  duration: string;
  type: string;
  thumbnail: string;
  isLocked?: boolean;
}

const VideoCard: React.FC<VideoCardProps> = ({
  id,
  title,
  duration,
  type,
  thumbnail,
  isLocked = false,
}) => {
  const navigate = useNavigate();
  const goToPricing = usePricingNav();
  const { isFavorited, toggle } = useFavorites();
  const favorited = isFavorited(id);

  const handleCardClick = () => {
    if (isLocked) {
      goToPricing();
      return;
    }
    navigate(`/membership/video/${id}`);
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggle(id);
  };

  return (
    <VideoCardWrapper onClick={handleCardClick} sx={{ cursor: "pointer" }}>
      <Thumbnail
        sx={{
          backgroundImage: `url(${thumbnail})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
          overflow: "hidden",
          borderRadius: "16px",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 50%)",
            opacity: 1,
          },
          "&:hover .play-icon": {
            opacity: 1,
            transform: "translate(-50%, -50%) scale(1)",
          },
          "&:hover": {
            transform: "scale(1.02)",
            boxShadow: "0 12px 24px rgba(0,0,0,0.18)",
          },
          transition: "all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)",
        }}
      >
        {/* Favorite Button */}
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 10,
          }}
        >
          <IconButton
            onClick={handleFavoriteClick}
            sx={{
              backgroundColor: "rgba(255,255,255,0.8)",
              backdropFilter: "blur(4px)",
              "&:hover": { backgroundColor: "white" },
              width: 36,
              height: 36,
            }}
          >
            {favorited ? (
              <Favorite sx={{ color: "#E53935", fontSize: "1.2rem" }} />
            ) : (
              <FavoriteBorder sx={{ color: MEMBERSHIP_COLORS.muted, fontSize: "1.2rem" }} />
            )}
          </IconButton>
        </Box>

        {/* Play Icon Overlay */}
        {!isLocked && (
          <Box
            className="play-icon"
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) scale(0.8)",
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              borderRadius: "50%",
              width: 56,
              height: 56,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0,
              transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
              zIndex: 2,
              color: MEMBERSHIP_COLORS.blue,
              boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
            }}
          >
            <PlayArrowRounded fontSize="large" />
          </Box>
        )}

        {/* Locked Badge */}
        {isLocked && (
          <Box
            sx={{
              position: "absolute",
              bottom: 12,
              left: 12,
              zIndex: 4,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              backgroundColor: "rgba(0,0,0,0.55)",
              borderRadius: "20px",
              px: 1.2,
              py: 0.4,
            }}
          >
            <LockOutlined sx={{ fontSize: 14, color: "white" }} />
            <Typography
              variant="caption"
              sx={{ color: "white", fontWeight: 700, fontSize: "0.65rem", letterSpacing: "0.05em" }}
            >
              LOCKED
            </Typography>
          </Box>
        )}

        <DurationTag sx={{ zIndex: 4, bottom: 12, right: 12 }}>
          {duration}
        </DurationTag>
      </Thumbnail>

      <Box sx={{ mt: 1.5, px: 0.5 }}>
        <Typography
          variant="overline"
          sx={{
            color: MEMBERSHIP_COLORS.blue,
            fontWeight: 800,
            mb: 0.5,
            display: "block",
            letterSpacing: "0.1em",
            fontSize: "0.7rem",
          }}
        >
          {type}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 700,
            fontSize: "0.95rem",
            lineHeight: 1.4,
            color: MEMBERSHIP_COLORS.navy,
            "&:hover": { color: MEMBERSHIP_COLORS.accent },
            transition: "color 0.2s ease",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {title}
        </Typography>
      </Box>
    </VideoCardWrapper>
  );
};

export default VideoCard;
